import type {
  CreateAddressInput,
  CreateCustomerInput,
  UpdateOrderInput,
  Customer,
} from '@haus-storefront-react/shared-types'

import type { AddressData, CustomerDetailsData } from '../types'
import type { CustomerDetailsFormValues } from '../steps/customer-details/customer-details.form'
import { normalizeAddressValue } from './address-normalization'

function createAddressInput(
  address: AddressData,
  options?: { defaultBillingAddress?: boolean; defaultShippingAddress?: boolean },
): CreateAddressInput {
  return {
    fullName: address.fullName || undefined,
    company: address.company || undefined,
    streetLine1: address.addressLine1,
    streetLine2: address.addressLine2 || undefined,
    city: address.city,
    province: address.province || undefined,
    postalCode: address.postalCode,
    countryCode: address.countryCode,
    phoneNumber: address.phoneNumber || undefined,
    defaultBillingAddress: options?.defaultBillingAddress,
    defaultShippingAddress: options?.defaultShippingAddress,
  }
}

export function createOrderInput(formValues: CustomerDetailsData): UpdateOrderInput {
  const hasSeparateBilling = Boolean(
    formValues.useSeparateBillingAddress && formValues.billingAddress,
  )

  const customer: CreateCustomerInput = {
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    emailAddress: formValues.emailAddress,
    phoneNumber: formValues.phoneNumber || undefined,
  }

  const shippingAddress: CreateAddressInput = createAddressInput(formValues.shippingAddress, {
    defaultBillingAddress: !hasSeparateBilling,
    defaultShippingAddress: true,
  })

  const result: UpdateOrderInput = {
    customer,
    shippingAddress,
  }

  if (hasSeparateBilling && formValues.billingAddress) {
    result.billingAddress = createAddressInput(formValues.billingAddress, {
      defaultBillingAddress: true,
      defaultShippingAddress: false,
    })
  } else {
    result.billingAddress = shippingAddress
  }

  return result
}

export function createOrderInputFromValues(values: CustomerDetailsFormValues): {
  data: CustomerDetailsData
  orderInput: UpdateOrderInput
} {
  const data: CustomerDetailsData = {
    firstName: values.firstName,
    lastName: values.lastName,
    phoneNumber: values.phoneNumber,
    emailAddress: values.emailAddress,
    useSeparateBillingAddress: values.useSeparateBillingAddress,
    shippingAddress: values.shippingAddress,
    ...(values.useSeparateBillingAddress && { billingAddress: values.billingAddress }),
  }

  return { data, orderInput: createOrderInput(data) }
}

const CUSTOMER_DEFAULT_ADDRESS_COMPARE_KEYS: Array<keyof AddressData> = [
  'addressLine1',
  'city',
  'postalCode',
  'countryCode',
]

const isSameAddress = (a: AddressData, b: AddressData): boolean =>
  CUSTOMER_DEFAULT_ADDRESS_COMPARE_KEYS.every(
    (key) => normalizeAddressValue(key, a[key]) === normalizeAddressValue(key, b[key]),
  )

/**
 * Create order input from contact info and customer default addresses.
 */
export function createOrderInputFromCustomer(
  activeCustomer: Customer,
  shippingAddress: AddressData,
  billingAddress?: AddressData,
): { data: CustomerDetailsData; orderInput: UpdateOrderInput } {
  const billing = billingAddress ?? shippingAddress
  const hasSeparateBilling = !isSameAddress(shippingAddress, billing)
  const data: CustomerDetailsData = {
    firstName: activeCustomer.firstName,
    lastName: activeCustomer.lastName,
    emailAddress: activeCustomer.emailAddress,
    phoneNumber: activeCustomer.phoneNumber ?? '',
    useSeparateBillingAddress: hasSeparateBilling,
    shippingAddress,
    ...(hasSeparateBilling && { billingAddress: billing }),
  }
  return { data, orderInput: createOrderInput(data) }
}
